/**
 * @file <bk-quantity-input>: the minus / plus stepper around a number input.
 *
 * Markup (snippets/cart-line-item.liquid, and any form that wants it):
 *   <bk-quantity-input>
 *     <button type="button" data-quantity-step="-1" aria-label="…">…</button>
 *     <input type="number" name="updates[]" value="1" min="1" step="1">
 *     <button type="button" data-quantity-step="1" aria-label="…">…</button>
 *   </bk-quantity-input>
 *
 * The element only steps within the input's own min / max / step (Shopify's `quantity_rule`),
 * disables the button that would leave that range, and fires a native `change` event (bubbling,
 * untrusted) so the cart (assets/cart-items.js) or a product form can react. Enter in the field
 * commits the typed value instead of submitting the form. Without JavaScript the input still
 * posts with its form.
 */

export class BkQuantityInput extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', (event) => this.#handleClick(event), { signal });
    this.addEventListener('keydown', (event) => this.#handleKeydown(event), { signal });
    this.addEventListener('change', () => this.#syncButtons(), { signal });
    this.#syncButtons();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @returns {HTMLInputElement | null} */
  get input() {
    const input = this.querySelector('input[type="number"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @returns {{ min: number, max: number, step: number }} */
  #limits() {
    const input = this.input;
    const min = input && input.min !== '' ? Number(input.min) : 0;
    const max = input && input.max !== '' ? Number(input.max) : Number.POSITIVE_INFINITY;
    const step = input && Number(input.step) > 0 ? Number(input.step) : 1;
    return { min, max, step };
  }

  /**
   * Step the value by one increment (snapped to the min + n × step grid) and fire `change` when
   * it really changed.
   * @param {number} direction - -1 or 1
   */
  step(direction) {
    const input = this.input;
    if (!input || !direction) return;

    const { min, max, step } = this.#limits();
    const current = Number.isFinite(Number(input.value)) && input.value !== '' ? Number(input.value) : min;
    const offset = (current - min) / step;
    const snapped = direction > 0 ? Math.floor(offset) + 1 : Math.ceil(offset) - 1;
    const next = Math.min(max, Math.max(min, min + snapped * step));

    if (next === current) {
      this.#syncButtons();
      return;
    }

    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @param {MouseEvent} event */
  #handleClick(event) {
    const target = event.target;
    const button = target instanceof Element ? target.closest('[data-quantity-step]') : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true') return;
    this.step(Math.sign(Number(button.dataset.quantityStep) || 0));
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown(event) {
    if (event.key !== 'Enter' || event.target !== this.input || event.isComposing) return;
    event.preventDefault();
    this.input?.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Mark the step that would leave the allowed range. aria-disabled (not `disabled`) keeps a
   * focused button focused when it reaches the limit, so keyboard users never lose their place.
   */
  #syncButtons() {
    const input = this.input;
    if (!input) return;

    const { min, max } = this.#limits();
    const current = input.value === '' ? min : Number(input.value);

    for (const button of this.querySelectorAll('[data-quantity-step]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const direction = Number(button.dataset.quantityStep) || 0;
      const atLimit = direction < 0 ? current <= min : current >= max;
      button.setAttribute('aria-disabled', String(atLimit));
    }
  }
}

if (!customElements.get('bk-quantity-input')) customElements.define('bk-quantity-input', BkQuantityInput);
